import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/layout/Header';
import SubscribeModal from '../components/SubscribeModal';
import UserNotificationBell from '../components/UserNotificationBell';
import { 
  Trophy, Video, Crown, ExternalLink, Trash2, Award, Flame, Shield, 
  Star, Repeat, Medal, TrendingUp, Target, Gamepad2, Clock, Calendar,
  ChevronRight, Zap, Gift, Users, CheckCircle, XCircle, Key, Package,
  DollarSign, Copy, MapPin, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [scheduledChallenges, setScheduledChallenges] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [myWins, setMyWins] = useState([]);
  const [leaderboardPosition, setLeaderboardPosition] = useState(null);
  const [userStats, setUserStats] = useState({
    totalPoints: 0,
    totalWins: 0,
    firstPlace: 0,
    secondPlace: 0,
    thirdPlace: 0,
    participations: 0,
    badges: []
  });
  const [latestWinner, setLatestWinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingPrize, setClaimingPrize] = useState(null);
  const [countdowns, setCountdowns] = useState({});
  const [shippingForm, setShippingForm] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'paypal',
    payment_info: ''
  });

  // Calculate remaining time for a challenge
  const calculateTimeRemaining = (endTime) => {
    if (!endTime) return { expired: true };
    try {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      if (isNaN(end)) return { expired: true };
      const diff = end - now;
      
      if (diff <= 0) return { expired: true };
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds, expired: false };
    } catch (e) {
      return { expired: true };
    }
  };

  // Update countdowns every second
  useEffect(() => {
    const interval = setInterval(() => {
      const newCountdowns = {};
      const safeActive = Array.isArray(activeChallenges) ? activeChallenges : [];
      safeActive.forEach(challenge => {
        if (challenge?.end_time) {
          newCountdowns[challenge.id] = calculateTimeRemaining(challenge.end_time);
        }
      });
      setCountdowns(newCountdowns);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeChallenges]);

  const fetchMyWins = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/my-wins`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setMyWins(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch wins:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          axios.get(`${API}/challenges/active`).catch(() => ({ data: [] })),
          axios.get(`${API}/challenges/scheduled`).catch(() => ({ data: [] })),
          token 
            ? axios.get(`${API}/submissions/mine`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
          axios.get(`${API}/leaderboard?limit=100`).catch(() => ({ data: [] })),
          axios.get(`${API}/winners/latest`).catch(() => ({ data: null }))
        ];

        const [
          activeRes, 
          scheduledRes, 
          submissionsRes, 
          leaderboardRes,
          latestWinnerRes
        ] = await Promise.all(promises);

        setActiveChallenges(Array.isArray(activeRes.data) ? activeRes.data : []);
        setScheduledChallenges(Array.isArray(scheduledRes.data) ? scheduledRes.data : []);
        
        // Handle submissions - could be single or array
        const subs = Array.isArray(submissionsRes.data) 
          ? submissionsRes.data 
          : submissionsRes.data ? [submissionsRes.data] : [];
        setMySubmissions(subs);
        
        setLatestWinner(latestWinnerRes.data);

        // Fetch my wins with full prize info
        if (token) {
          await fetchMyWins();
        }

        // Calculate user stats from leaderboard
        const leaderboard = Array.isArray(leaderboardRes.data) ? leaderboardRes.data : [];
        const myPosition = user?.id ? leaderboard.findIndex(e => e.user_id === user.id) : -1;
        if (myPosition !== -1) {
          setLeaderboardPosition({
            rank: myPosition + 1,
            total: leaderboard.length,
            entry: leaderboard[myPosition]
          });
          setUserStats({
            totalPoints: leaderboard[myPosition].score || 0,
            totalWins: leaderboard[myPosition].wins_count || 0,
            firstPlace: leaderboard[myPosition].first_place_wins || 0,
            secondPlace: leaderboard[myPosition].second_place_wins || 0,
            thirdPlace: leaderboard[myPosition].third_place_wins || 0,
            participations: leaderboard[myPosition].participation_count || subs.length,
            badges: Array.isArray(leaderboard[myPosition].badges) ? leaderboard[myPosition].badges : []
          });
        }

      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id]);

  const handleClaimPrize = async (winnerId, prizeType) => {
    // For item or money prizes, show the form first
    if (prizeType === 'item' || prizeType === 'money') {
      setClaimingPrize(winnerId);
      return;
    }
    
    // For game codes, claim immediately
    try {
      await axios.post(`${API}/my-wins/${winnerId}/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Game code revealed!');
      await fetchMyWins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim prize');
    }
  };

  const handleSubmitPayment = async (winnerId) => {
    if (!paymentForm.payment_info.trim()) {
      toast.error(`Please enter your ${paymentForm.payment_method === 'paypal' ? 'PayPal email' : 'CashApp username'}`);
      return;
    }
    
    try {
      await axios.post(`${API}/my-wins/${winnerId}/claim`, paymentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prize claimed! You will receive payment soon.');
      setClaimingPrize(null);
      setPaymentForm({ payment_method: 'paypal', payment_info: '' });
      await fetchMyWins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim prize');
    }
  };

  const handleSubmitShipping = async (winnerId) => {
    try {
      await axios.post(`${API}/my-wins/${winnerId}/shipping`, shippingForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Shipping address submitted! Your prize will be shipped soon.');
      setClaimingPrize(null);
      setShippingForm({
        full_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: ''
      });
      await fetchMyWins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit shipping address');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      await axios.delete(`${API}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySubmissions(prev => prev.filter(s => s.id !== submissionId));
      toast.success('Submission deleted successfully');
    } catch (error) {
      // Try alternative endpoint
      try {
        await axios.delete(`${API}/submissions/mine`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMySubmissions([]);
        toast.success('Submission deleted successfully');
      } catch (e) {
        toast.error('Failed to delete submission');
      }
    }
  };

  // Get challenges user hasn't submitted to yet
  const availableChallenges = activeChallenges.filter(
    c => !mySubmissions.some(s => s.challenge_id === c.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <main className="pt-20 sm:pt-24 pb-12 px-3 sm:px-4 max-w-7xl mx-auto">
        {/* User Profile Header */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8 p-4 sm:p-6 rounded-xl bg-gradient-to-r from-red-950/30 to-purple-950/30 border border-red-900/30">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              <img 
                src={user?.profile_image_url || 'https://via.placeholder.com/80'} 
                alt={user?.display_name}
                className="w-14 h-14 sm:w-20 sm:h-20 rounded-full border-4 border-purple-500 shadow-lg shadow-purple-500/30"
              />
              {leaderboardPosition && leaderboardPosition.rank <= 3 && (
                <div className={`absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-black font-bold text-xs sm:text-sm ${
                  leaderboardPosition.rank === 1 ? 'bg-yellow-400' :
                  leaderboardPosition.rank === 2 ? 'bg-gray-300' : 'bg-amber-600'
                }`}>
                  #{leaderboardPosition.rank}
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-lg sm:text-2xl md:text-3xl font-bold truncate" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {user?.display_name}
                </h1>
                {isAdmin && (
                  <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-[10px] sm:text-xs font-bold uppercase border border-yellow-500/30">
                    Admin
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm">@{user?.twitch_username}</p>
            </div>
          </div>
          
          {/* Quick Stats - Mobile optimized */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-3 border-t border-white/10">
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-black text-green-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {userStats.totalPoints}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Points</p>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-black text-yellow-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {userStats.totalWins}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Wins</p>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl font-black text-purple-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                #{leaderboardPosition?.rank || '-'}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Rank</p>
            </div>
          </div>
          
          {/* Badges & Subscribe Button */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {userStats.badges.map((badge) => (
              <span 
                key={badge.id}
                className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                  badge.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                  badge.color === 'gold' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                  badge.color === 'purple' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                  badge.color === 'blue' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                  badge.color === 'orange' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                  badge.color === 'red' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                  'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}
              >
                {badge.icon === 'medal' && <Medal className="w-3 h-3" />}
                {badge.icon === 'trophy' && <Trophy className="w-3 h-3" />}
                {badge.icon === 'star' && <Star className="w-3 h-3" />}
                {badge.icon === 'repeat' && <Repeat className="w-3 h-3" />}
                {badge.icon === 'flame' && <Flame className="w-3 h-3" />}
                {badge.icon === 'shield' && <Shield className="w-3 h-3" />}
                {badge.name}
              </span>
            ))}
            {/* Notification Bell & Action Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              <UserNotificationBell token={token} />
              <a
                href="https://tarkov.dev/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-1 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 text-xs rounded-full border border-orange-500/30 flex items-center gap-1 transition-colors"
                data-testid="dashboard-tarkov-btn"
              >
                <Gamepad2 className="w-3 h-3" /> Tarkov Info
              </a>
              <Button 
                variant="ghost"
                size="sm"
                className="px-2 py-1 text-green-400 hover:text-green-300 hover:bg-green-500/10 text-xs rounded-full border border-green-500/30"
                onClick={() => setShowSubscribe(true)}
                data-testid="dashboard-subscribe-btn"
              >
                <Bell className="w-3 h-3 mr-1" /> Get Notified
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <Card className="glass-card border-yellow-500/30 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <span className="text-lg sm:text-xl">🥇</span>
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-yellow-400">{userStats.firstPlace}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">1st Place</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-gray-500/30 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-500/20 flex items-center justify-center">
                <span className="text-lg sm:text-xl">🥈</span>
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-300">{userStats.secondPlace}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">2nd Place</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-amber-500/30 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-lg sm:text-xl">🥉</span>
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-amber-500">{userStats.thirdPlace}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">3rd Place</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-blue-500/30 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Video className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-blue-400">{userStats.participations}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">Submissions</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Active & Available Challenges */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Active Challenges - Only shows when there are active challenges */}
            {activeChallenges.length > 0 && (
              <Card className="glass-card border-green-500/30">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="flex items-center gap-2 text-green-400 text-base sm:text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                    Active Challenges
                    <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      {activeChallenges.length} Live
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  {activeChallenges.map((challenge) => {
                    const hasSubmitted = mySubmissions.some(s => s.challenge_id === challenge.id);
                    const countdown = countdowns[challenge.id];
                    return (
                      <div key={challenge.id} className="p-3 sm:p-4 bg-black/40 rounded-lg border border-green-500/20">
                        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                          {challenge.game_cover_url && (
                            <img 
                              src={challenge.game_cover_url} 
                              alt={challenge.game_name}
                              className="w-full sm:w-16 h-32 sm:h-20 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 w-full">
                            <h3 className="font-bold text-white mb-1 text-sm sm:text-base">{challenge.title}</h3>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                ${challenge.prize_amount}
                              </span>
                              {challenge.game_name && (
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded flex items-center gap-1">
                                  <Gamepad2 className="w-3 h-3" />
                                  {challenge.game_name}
                                </span>
                              )}
                              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                                {challenge.max_winners || 1} winner{(challenge.max_winners || 1) > 1 ? 's' : ''}
                              </span>
                            </div>
                            
                            {/* Countdown Timer */}
                            {countdown && !countdown.expired && (
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <Clock className="w-4 h-4 text-red-400 animate-pulse" />
                                <span className="text-xs text-gray-400">Ends in:</span>
                                <div className="flex gap-1">
                                  {countdown.days > 0 && (
                                    <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded">
                                      {countdown.days}d
                                    </span>
                                  )}
                                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded">
                                    {String(countdown.hours).padStart(2, '0')}h
                                  </span>
                                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded">
                                    {String(countdown.minutes).padStart(2, '0')}m
                                  </span>
                                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded animate-pulse">
                                    {String(countdown.seconds).padStart(2, '0')}s
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {challenge.rules && (
                              <p className="text-xs text-gray-400 mb-2 line-clamp-2">{challenge.rules}</p>
                            )}
                            {hasSubmitted ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                <Video className="w-3 h-3" /> Submitted
                              </span>
                            ) : (
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                                onClick={() => navigate('/submit')}
                              >
                                <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Submit Now
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Scheduled Challenges */}
            {scheduledChallenges.length > 0 && (
              <Card className="glass-card border-yellow-500/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-yellow-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    <Calendar className="w-5 h-5" />
                    Upcoming Challenges
                    <span className="ml-auto px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {scheduledChallenges.length} Scheduled
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {scheduledChallenges.slice(0, 3).map((challenge) => (
                    <div key={challenge.id} className="p-4 bg-black/40 rounded-lg border border-yellow-500/20">
                      <div className="flex items-start gap-4">
                        {challenge.game_cover_url && (
                          <img 
                            src={challenge.game_cover_url} 
                            alt={challenge.game_name}
                            className="w-12 h-16 object-cover rounded opacity-70"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-white mb-1">{challenge.title}</h3>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                              ${challenge.prize_amount}
                            </span>
                            {challenge.game_name && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
                                {challenge.game_name}
                              </span>
                            )}
                          </div>
                          {challenge.start_time && (
                            <p className="text-xs text-yellow-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Starts: {new Date(challenge.start_time).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* My Submissions */}
            <Card className="glass-card border-blue-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <Video className="w-5 h-5" />
                  My Submissions
                  <span className="ml-auto px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                    {mySubmissions.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mySubmissions.length > 0 ? (
                  <div className="space-y-3">
                    {mySubmissions.map((sub) => {
                      const status = sub.status || 'pending';
                      const statusConfig = {
                        pending: { 
                          color: 'yellow', 
                          bg: 'bg-yellow-500/20', 
                          border: 'border-yellow-500/30',
                          text: 'text-yellow-400',
                          label: 'Pending Review',
                          icon: <Clock className="w-3 h-3" />
                        },
                        approved: { 
                          color: 'green', 
                          bg: 'bg-green-500/20', 
                          border: 'border-green-500/30',
                          text: 'text-green-400',
                          label: 'Approved',
                          icon: <Award className="w-3 h-3" />
                        },
                        rejected: { 
                          color: 'red', 
                          bg: 'bg-red-500/20', 
                          border: 'border-red-500/30',
                          text: 'text-red-400',
                          label: 'Rejected',
                          icon: <Trash2 className="w-3 h-3" />
                        }
                      };
                      const statusInfo = statusConfig[status] || statusConfig.pending;

                      return (
                        <div 
                          key={sub.id} 
                          className={`p-3 bg-black/40 rounded-lg border ${statusInfo.border} ${status === 'rejected' ? 'opacity-60' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            {/* Status Badge */}
                            <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                              {statusInfo.icon}
                              {statusInfo.label}
                            </span>
                            <div className="flex items-center gap-2">
                              <a 
                                href={sub.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-white/10 rounded"
                              >
                                <ExternalLink className="w-4 h-4 text-gray-400" />
                              </a>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded flex items-center justify-center ${
                              sub.video_type === 'twitch' ? 'bg-purple-500/20' : 'bg-red-500/20'
                            }`}>
                              <Video className={`w-5 h-5 ${sub.video_type === 'twitch' ? 'text-purple-400' : 'text-red-400'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-white truncate">{sub.video_title || 'Untitled'}</p>
                              <p className="text-xs text-gray-500">
                                {sub.challenge_title && <span className="text-blue-400">{sub.challenge_title} • </span>}
                                {sub.video_type}
                              </p>
                            </div>
                          </div>

                          {/* Rejection Reason */}
                          {status === 'rejected' && sub.rejection_reason && (
                            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded">
                              <p className="text-xs text-red-400">
                                <strong>Reason:</strong> {sub.rejection_reason}
                              </p>
                            </div>
                          )}

                          {/* Pending message */}
                          {status === 'pending' && (
                            <p className="text-xs text-yellow-400/70 mt-2">
                              Your submission is being reviewed by our team.
                            </p>
                          )}

                          {/* Approved message */}
                          {status === 'approved' && (
                            <p className="text-xs text-green-400/70 mt-2">
                              Your submission is live in the gallery!
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Video className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-4">No submissions yet</p>
                    {availableChallenges.length > 0 && (
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => navigate('/submit')}
                      >
                        <Video className="w-4 h-4 mr-2" /> Submit Your First Video
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Wins & Leaderboard */}
          <div className="space-y-6">
            {/* Leaderboard Position */}
            {leaderboardPosition && (
              <Card className="glass-card border-purple-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-purple-400 text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    <TrendingUp className="w-5 h-5" />
                    Your Ranking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <div className="text-5xl font-black text-purple-400 mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                      #{leaderboardPosition.rank}
                    </div>
                    <p className="text-gray-500 text-sm">out of {leaderboardPosition.total} players</p>
                  </div>
                  <Progress 
                    value={100 - ((leaderboardPosition.rank / leaderboardPosition.total) * 100)} 
                    className="h-2 mb-4"
                  />
                  <Button 
                    variant="outline"
                    className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    onClick={() => navigate('/leaderboard')}
                  >
                    View Full Leaderboard
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* My Wins & Prizes */}
            <Card className="glass-card border-yellow-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-yellow-400 text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <Crown className="w-5 h-5" />
                  My Wins & Prizes
                  {myWins.length > 0 && (
                    <span className="ml-auto px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {myWins.length}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myWins.length > 0 ? (
                  <div className="space-y-3">
                    {myWins.slice(0, 5).map((win) => {
                      const prizeInfo = win.prize_info || {};
                      const prizeType = prizeInfo.prize_type || 'money';
                      const isClaimed = prizeInfo.claimed;
                      
                      return (
                        <div key={win.id} className="p-3 bg-black/40 rounded-lg border border-yellow-500/20">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">
                              {win.place === 1 ? '🥇' : win.place === 2 ? '🥈' : '🥉'}
                            </span>
                            
                            {/* Prize Type Badge */}
                            {prizeType === 'money' && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                <DollarSign className="w-3 h-3" />
                                ${prizeInfo.amount || win.prize_amount}
                              </span>
                            )}
                            {prizeType === 'game_code' && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                                <Key className="w-3 h-3" />
                                Game Code
                              </span>
                            )}
                            {prizeType === 'item' && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
                                <Gift className="w-3 h-3" />
                                Item
                              </span>
                            )}
                            
                            <span className="text-xs text-gray-500 ml-auto">+{win.points}pts</span>
                          </div>
                          
                          {/* Prize Description */}
                          {prizeInfo.description && (
                            <p className="text-sm text-white mb-2">{prizeInfo.description}</p>
                          )}
                          
                          {win.challenge_title && (
                            <p className="text-xs text-blue-400 mb-1">{win.challenge_title}</p>
                          )}
                          
                          {/* Claim Status & Actions */}
                          {isClaimed ? (
                            <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded">
                              <div className="flex items-center gap-2 text-green-400 text-xs">
                                <CheckCircle className="w-4 h-4" />
                                Prize Claimed
                              </div>
                              
                              {/* Show code for game_code prizes */}
                              {prizeType === 'game_code' && prizeInfo.code && (
                                <div className="mt-2">
                                  <Label className="text-xs text-gray-400">Your Code:</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <code className="flex-1 p-2 bg-black/50 rounded font-mono text-sm text-blue-400 break-all">
                                      {prizeInfo.code}
                                    </code>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 border-blue-500/30 text-blue-400"
                                      onClick={() => copyToClipboard(prizeInfo.code)}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mt-2">
                              {prizeType === 'money' && claimingPrize !== win.id && (
                                <Button
                                  size="sm"
                                  className="w-full bg-green-600 hover:bg-green-700"
                                  onClick={() => handleClaimPrize(win.id, prizeType)}
                                >
                                  <DollarSign className="w-4 h-4 mr-1" />
                                  Claim Cash Prize
                                </Button>
                              )}
                              
                              {/* Payment Form for Cash Prizes */}
                              {prizeType === 'money' && claimingPrize === win.id && (
                                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded space-y-3">
                                  <Label className="text-xs text-green-400 font-bold">How would you like to receive payment?</Label>
                                  
                                  {/* Payment Method Selection */}
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant={paymentForm.payment_method === 'paypal' ? 'default' : 'outline'}
                                      className={paymentForm.payment_method === 'paypal' 
                                        ? 'flex-1 bg-blue-600 hover:bg-blue-700' 
                                        : 'flex-1 border-gray-600'}
                                      onClick={() => setPaymentForm({...paymentForm, payment_method: 'paypal', payment_info: ''})}
                                    >
                                      PayPal
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={paymentForm.payment_method === 'cashapp' ? 'default' : 'outline'}
                                      className={paymentForm.payment_method === 'cashapp' 
                                        ? 'flex-1 bg-green-600 hover:bg-green-700' 
                                        : 'flex-1 border-gray-600'}
                                      onClick={() => setPaymentForm({...paymentForm, payment_method: 'cashapp', payment_info: ''})}
                                    >
                                      CashApp
                                    </Button>
                                  </div>
                                  
                                  {/* Payment Info Input */}
                                  <Input
                                    placeholder={paymentForm.payment_method === 'paypal' 
                                      ? 'Enter your PayPal email' 
                                      : 'Enter your $CashTag (e.g., $username)'}
                                    value={paymentForm.payment_info}
                                    onChange={(e) => setPaymentForm({...paymentForm, payment_info: e.target.value})}
                                    className="h-9 bg-black/50 border-green-500/30"
                                  />
                                  
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="flex-1"
                                      onClick={() => setClaimingPrize(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="flex-1 bg-green-600 hover:bg-green-700"
                                      onClick={() => handleSubmitPayment(win.id)}
                                      disabled={!paymentForm.payment_info.trim()}
                                    >
                                      Claim ${prizeInfo.amount || win.prize_amount}
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {prizeType === 'game_code' && (
                                <Button
                                  size="sm"
                                  className="w-full bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleClaimPrize(win.id, prizeType)}
                                >
                                  <Key className="w-4 h-4 mr-1" />
                                  Reveal Game Code
                                </Button>
                              )}
                              
                              {prizeType === 'item' && claimingPrize !== win.id && (
                                <Button
                                  size="sm"
                                  className="w-full bg-purple-600 hover:bg-purple-700"
                                  onClick={() => handleClaimPrize(win.id, prizeType)}
                                >
                                  <MapPin className="w-4 h-4 mr-1" />
                                  Enter Shipping Address
                                </Button>
                              )}
                              
                              {/* Shipping Form for Items */}
                              {prizeType === 'item' && claimingPrize === win.id && (
                                <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded space-y-2">
                                  <Label className="text-xs text-purple-400 font-bold">Shipping Address</Label>
                                  <Input
                                    placeholder="Full Name"
                                    value={shippingForm.full_name}
                                    onChange={(e) => setShippingForm({...shippingForm, full_name: e.target.value})}
                                    className="h-8 bg-black/50 border-purple-500/30"
                                  />
                                  <Input
                                    placeholder="Address Line 1"
                                    value={shippingForm.address_line1}
                                    onChange={(e) => setShippingForm({...shippingForm, address_line1: e.target.value})}
                                    className="h-8 bg-black/50 border-purple-500/30"
                                  />
                                  <Input
                                    placeholder="Address Line 2 (optional)"
                                    value={shippingForm.address_line2}
                                    onChange={(e) => setShippingForm({...shippingForm, address_line2: e.target.value})}
                                    className="h-8 bg-black/50 border-purple-500/30"
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      placeholder="City"
                                      value={shippingForm.city}
                                      onChange={(e) => setShippingForm({...shippingForm, city: e.target.value})}
                                      className="h-8 bg-black/50 border-purple-500/30"
                                    />
                                    <Input
                                      placeholder="State"
                                      value={shippingForm.state}
                                      onChange={(e) => setShippingForm({...shippingForm, state: e.target.value})}
                                      className="h-8 bg-black/50 border-purple-500/30"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      placeholder="Postal Code"
                                      value={shippingForm.postal_code}
                                      onChange={(e) => setShippingForm({...shippingForm, postal_code: e.target.value})}
                                      className="h-8 bg-black/50 border-purple-500/30"
                                    />
                                    <Input
                                      placeholder="Country"
                                      value={shippingForm.country}
                                      onChange={(e) => setShippingForm({...shippingForm, country: e.target.value})}
                                      className="h-8 bg-black/50 border-purple-500/30"
                                    />
                                  </div>
                                  <Input
                                    placeholder="Phone (optional)"
                                    value={shippingForm.phone}
                                    onChange={(e) => setShippingForm({...shippingForm, phone: e.target.value})}
                                    className="h-8 bg-black/50 border-purple-500/30"
                                  />
                                  <div className="flex gap-2 pt-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="flex-1"
                                      onClick={() => setClaimingPrize(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                                      onClick={() => handleSubmitShipping(win.id)}
                                    >
                                      Submit Address
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {myWins.length > 5 && (
                      <Button 
                        variant="ghost"
                        className="w-full text-yellow-400"
                        onClick={() => navigate('/winners')}
                      >
                        View All Wins
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Crown className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No wins yet</p>
                    <p className="text-gray-500 text-xs mt-1">Keep participating to win!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Latest Winner */}
            {latestWinner && latestWinner.user_id !== user?.id && (
              <Card className="glass-card border-green-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-green-400 text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    <Gift className="w-5 h-5" />
                    Latest Winner
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <img 
                      src={latestWinner.profile_image_url || 'https://via.placeholder.com/48'} 
                      alt={latestWinner.display_name}
                      className="w-12 h-12 rounded-full border-2 border-yellow-500"
                    />
                    <div>
                      <p className="font-bold text-white">{latestWinner.display_name}</p>
                      <p className="text-green-400 font-bold">${latestWinner.prize_amount}</p>
                      {latestWinner.game_name && (
                        <p className="text-xs text-purple-400">{latestWinner.game_name}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="glass-card border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-red-400 text-base sm:text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <Target className="w-4 h-4 sm:w-5 sm:h-5" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-1 gap-2">
                {availableChallenges.length > 0 && (
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-xs sm:text-sm col-span-2 sm:col-span-1"
                    onClick={() => navigate('/submit')}
                  >
                    <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Submit Video
                  </Button>
                )}
                <Button 
                  variant="outline"
                  className="w-full border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 text-xs sm:text-sm"
                  onClick={() => navigate('/winners')}
                >
                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Hall of </span>Winners
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10 text-xs sm:text-sm"
                  onClick={() => navigate('/leaderboard')}
                >
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Leaderboard
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs sm:text-sm"
                  onClick={() => navigate('/history')}
                >
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> History
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs sm:text-sm"
                  onClick={() => navigate('/gallery')}
                >
                  <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Gallery
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Subscribe Modal */}
      <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} />
    </div>
  );
};

export default Dashboard;
